import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Box,
  Link,
  Checkbox,
  FormControlLabel,
  Tooltip,
  useTheme,
  type SxProps,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FacebookIcon from "@mui/icons-material/Facebook";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LiaQrcodeSolid } from "react-icons/lia";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import whatsappLogo from "../../../assets/images/whatsapp.svg";
import { QrCodeDisplay } from "./QrCodeDisplay";
import { useTranslation } from "react-i18next";
import {
  connectWebSocket,
  handleWebSocketMessage,
  handleWebSocketError,
  handleWebSocketClose,
} from "../../../utils/webSocketUtils";
import { URLS } from "../../../constants";
import { modalStyles } from "../../../theme/modalStyles";
import api from "../../../utils/api";
import { useDashboardTheme } from "../context/DashboardThemeContext";
import logger from "../../../utils/logger";
import { useWhatsAppEmbeddedSignup } from "../../../hooks/useWhatsAppEmbeddedSignup";
import WebhookConfigDialog from "./WebhookConfigDialog";

interface AccountNameFormProps {
  isOpen: boolean;
  toggle: () => void;
  updateSessions?: () => void;
  channel?: string;
  companyPhone?: string;
  onAllModalsClose?: () => void;
}

type State = {
  name: string;
  whatsappBusinessID: string;
  whatsappPhoneID: string;
  accessToken: string;
  isNewPhoneNumber: boolean;
  registerPin: string;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  attemptedSubmit: boolean;
  buttonBlocked: boolean;
  qrCode: string | null;
  showQrCode: boolean;
  webSocketLoading: boolean;
  status: string | null;
  showQrWarning: boolean;
  nameError: string | null;
};

const initialState: State = {
  name: "",
  whatsappBusinessID: "",
  whatsappPhoneID: "",
  accessToken: "",
  isNewPhoneNumber: false,
  registerPin: "",
  loading: false,
  error: null,
  successMessage: null,
  attemptedSubmit: false,
  buttonBlocked: false,
  qrCode: null,
  showQrCode: false,
  webSocketLoading: true,
  status: null,
  showQrWarning: false,
  nameError: null,
};

export const AccountNameForm: React.FC<AccountNameFormProps> = ({
  isOpen,
  toggle,
  updateSessions,
  channel,
  companyPhone,
  onAllModalsClose,
}): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDark = useDashboardTheme();

  const darkPaper: SxProps<Theme> = isDark
    ? {
        bgcolor: "rgba(20, 20, 20, 0.98)",
        border: "1px solid rgba(255,255,255,0.08)",
      }
    : {};
  const textMuted = isDark ? "#a0a0a0" : "#666";
  const textFieldSx: SxProps<Theme> = isDark
    ? {
        "& .MuiInputLabel-root": { color: "#a0a0a0" },
        "& .MuiInputLabel-root.Mui-focused": { color: "#667eea" },
        "& .MuiInputBase-input": { color: "#ffffff" },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.2)",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255,255,255,0.4)",
        },
        "& .MuiFormHelperText-root": { color: "#a0a0a0" },
      }
    : {};

  const [state, setState] = useState<State>(initialState);
  const [useManualSetup, setUseManualSetup] = useState(false);
  const [showWebhookConfig, setShowWebhookConfig] = useState(false);
  const socket = useRef<WebSocket | null>(null);
  const { launchSignup } = useWhatsAppEmbeddedSignup();

  //  Corrected WebSocket endpoint for QR Code (backend: connection.go)
  const WS_URL = `${URLS.WEBSOCKET_URL}/api/ws`;

  useEffect(() => {
    return () => {
      handleWebSocketClose(socket.current);
    };
  }, []);

  const handleToggle = () => {
    toggle();
    setState(initialState);
    setUseManualSetup(false);
  };

  const handleDialogClose = (_: object, reason: string) => {
    if (
      (reason === "backdropClick" || reason === "escapeKeyDown") &&
      state.showQrCode
    ) {
      setState((prev) => ({ ...prev, showQrWarning: true }));
    } else {
      handleToggle();
    }
  };

  const handleConfirmCloseWarning = () => {
    setState((prev) => ({ ...prev, showQrWarning: false }));
    handleToggle();
  };

  const validateName = (): boolean => {
    if (!state.name.trim()) {
      setState((prev) => ({
        ...prev,
        nameError: t("Session name is required"),
      }));
      return false;
    }

    if (state.name.trim().length < 3) {
      setState((prev) => ({
        ...prev,
        nameError: t(
          "The account name must be at least 3 characters long, cannot contain emojis, or be composed solely of numbers.",
        ),
      }));
      return false;
    }

    return true;
  };

  const handleSubmitMetaEmbedded = async (e: React.FormEvent) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, attemptedSubmit: true }));

    if (!validateName()) {
      return;
    }

    if (state.loading || state.buttonBlocked) {
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      buttonBlocked: true,
      error: null,
      nameError: null,
    }));

    try {
      const { code, wabaId, phoneNumberId } = await launchSignup();

      await api.post("/api/starting", {
        name: state.name,
        channel,
        whatsapp_business: wabaId,
        whatsapp_phone_id: phoneNumberId,
        embedded_signup_code: code,
        phone: companyPhone,
      });

      setState((prev) => ({
        ...prev,
        successMessage: t("WhatsApp Business session created successfully!"),
        loading: false,
        buttonBlocked: false,
      }));
      updateSessions?.();
      setShowWebhookConfig(true);
    } catch (err) {
      logger.error("[NewProfile] Error creating Meta session:", err);

      const errorMessage =
        err instanceof Error &&
        err.message === "User cancelled login or did not fully authorize."
          ? t("Login was cancelled. Please try again.")
          : axios.isAxiosError(err) && err.response?.status === 409
            ? t(
                "A session with this name already exists. Please choose a different name.",
              )
            : t("An error occurred while creating the Facebook session.");

      setState((prev) => ({
        ...prev,
        error: errorMessage,
        loading: false,
        buttonBlocked: false,
      }));
    }
  };

  const handleSubmitMetaManual = async (e: React.FormEvent) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, attemptedSubmit: true }));

    if (!validateName()) {
      return;
    }

    if (
      !state.whatsappBusinessID.trim() ||
      !state.whatsappPhoneID.trim() ||
      !state.accessToken.trim()
    ) {
      setState((prev) => ({
        ...prev,
        error: t("All fields are required for manual setup."),
      }));
      return;
    }

    if (state.isNewPhoneNumber && state.registerPin.length !== 6) {
      setState((prev) => ({
        ...prev,
        error: t("PIN must be exactly 6 digits."),
      }));
      return;
    }

    if (state.loading || state.buttonBlocked) {
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      buttonBlocked: true,
      error: null,
      nameError: null,
    }));
    try {
      await api.post("/api/starting", {
        name: state.name,
        channel,
        whatsapp_business: state.whatsappBusinessID,
        whatsapp_phone_id: state.whatsappPhoneID,
        access_token: state.accessToken,
        phone: companyPhone,
        ...(state.isNewPhoneNumber && state.registerPin
          ? { register_pin: state.registerPin }
          : {}),
      });

      setState((prev) => ({
        ...prev,
        successMessage: t("Facebook session created successfully!"),
        loading: false,
        buttonBlocked: false,
      }));
      updateSessions?.();
      setShowWebhookConfig(true);
    } catch (err) {
      logger.error("[NewProfile] Error creating Meta session:", err);
      let errorMessage = t(
        "An error occurred while creating the Facebook session.",
      );

      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const backendError = err.response?.data?.error ?? "";

        if (status === 409) {
          errorMessage = t(
            "A session with this name already exists. Please choose a different name.",
          );
        } else if (backendError.includes("failed to verify phone number")) {
          errorMessage = t(
            "Could not verify the phone number. Please check that your Access Token and Phone Number ID are correct.",
          );
        } else if (backendError.includes("failed to register phone number")) {
          errorMessage = t(
            "Could not register the phone number with Meta. Please check your Access Token, Phone Number ID, Business ID, and PIN.",
          );
        } else if (backendError.includes("failed to encrypt access token")) {
          errorMessage = t(
            "Internal error while securing your token. Please try again or contact support.",
          );
        }
      }

      setState((prev) => ({
        ...prev,
        error: errorMessage,
        loading: false,
        buttonBlocked: false,
      }));
    }
  };

  const handleSubmitDefault = async (e: React.FormEvent) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, attemptedSubmit: true }));

    if (!validateName()) {
      return;
    }

    if (state.loading || state.buttonBlocked) {
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      buttonBlocked: true,
      error: null,
      nameError: null,
    }));
    try {
      await api.post("/api/starting", {
        name: state.name,
        channel,
        whatsapp_business: "",
        whatsapp_phone_id: "",
      });

      setState((prev) => ({
        ...prev,
        successMessage: t("Session created successfully!"),
        loading: false,
        buttonBlocked: false,
        showQrCode: true,
      }));
      updateSessions?.();

      socket.current = connectWebSocket(
        WS_URL,
        (event) =>
          handleWebSocketMessage(
            event,
            setState,
            t("QR code successfully scanned!"),
            () => {
              // Update sessions immediately
              if (updateSessions) {
                updateSessions();
              }

              // Close modal after a short delay to show success message
              setTimeout(() => {
                // Reset internal state first
                setState(initialState);

                // Close all modals using the parent callback
                if (onAllModalsClose) {
                  onAllModalsClose();
                } else {
                  // Fallback to toggle if onAllModalsClose is not available
                  toggle();
                }
              }, 1500);
            },
          ),
        (event) =>
          handleWebSocketError(event, setState, t("WebSocket error observed")),
      );
    } catch (err) {
      logger.error("[NewProfile] Error creating session:", err);
      const errorMessage =
        axios.isAxiosError(err) && err.response?.status === 409
          ? t(
              "A session with this name already exists. Please choose a different name.",
            )
          : t("An error occurred while creating the session.");

      setState((prev) => ({
        ...prev,
        error: errorMessage,
        loading: false,
        buttonBlocked: false,
      }));
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: modalStyles.paper, sx: darkPaper }}
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box sx={modalStyles.header(theme)}>
            <Typography variant="h6" sx={{ color: "#fff" }}>
              {t("Criar sessão")}
            </Typography>
            <IconButton
              onClick={handleToggle}
              sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </motion.div>

        <DialogContent sx={modalStyles.content}>
          <Container
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 1,
              position: "relative",
              minHeight: "400px",
            }}
          >
            <AnimatePresence mode="wait">
              {state.loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "400px",
                  }}
                >
                  <CircularProgress size={60} />
                  <Typography sx={{ mt: 2, color: textMuted }}>
                    {t("Creating session...")}
                  </Typography>
                </motion.div>
              ) : state.showQrCode && state.qrCode ? (
                <motion.div
                  key="qrcode"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  style={{ width: "100%" }}
                >
                  <Box mb={2} display="flex" justifyContent="center">
                    <img src={whatsappLogo} alt="WhatsApp logo" width={80} />
                  </Box>
                  <Typography align="center" sx={{ mb: 1, fontWeight: 600 }}>
                    {t("Scan the QR code to connect")}
                  </Typography>
                  <Typography
                    align="center"
                    sx={{ mb: 3, fontSize: 14, color: textMuted }}
                  >
                    {t("Point your phone's camera at the QR code")}
                  </Typography>
                  <QrCodeDisplay qrCode={state.qrCode} />
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  style={{ width: "100%" }}
                >
                  <Box mb={2} display="flex" justifyContent="center">
                    <img src={whatsappLogo} alt="WhatsApp logo" width={80} />
                  </Box>
                  <Typography
                    align="center"
                    sx={{ mb: 1, color: isDark ? "#ffffff" : undefined }}
                  >
                    {t("Enter your new account here")}
                  </Typography>
                  <Typography
                    align="center"
                    sx={{ mb: 2, fontSize: 14, color: textMuted }}
                  >
                    {t(
                      "This name will appear so you know which account is connected.",
                    )}
                  </Typography>

                  <Box
                    component="form"
                    onSubmit={
                      channel !== "API_OFFICIAL"
                        ? handleSubmitDefault
                        : useManualSetup
                          ? handleSubmitMetaManual
                          : handleSubmitMetaEmbedded
                    }
                    sx={{ width: "100%" }}
                  >
                    {state.error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {state.error}
                      </Alert>
                    )}
                    <TextField
                      label={t("Account Name")}
                      fullWidth
                      size="small"
                      margin="dense"
                      value={state.name}
                      onChange={(e) =>
                        setState((prev) => ({
                          ...prev,
                          name: e.target.value,
                          nameError: null,
                        }))
                      }
                      error={!!state.nameError}
                      helperText={state.nameError}
                      placeholder={t("Account Name")}
                      autoFocus
                      sx={textFieldSx}
                    />
                    {channel === "API_OFFICIAL" && !useManualSetup && (
                      <Typography
                        align="center"
                        sx={{ mt: 2, fontSize: 14, color: textMuted }}
                      >
                        {t(
                          "You will be redirected to connect your WhatsApp Business account via Facebook.",
                        )}
                      </Typography>
                    )}
                    {channel === "API_OFFICIAL" && useManualSetup && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <TextField
                            label={t("Whatsapp Business ID")}
                            fullWidth
                            size="small"
                            margin="dense"
                            sx={textFieldSx}
                            value={state.whatsappBusinessID}
                            onChange={(e) =>
                              setState((prev) => ({
                                ...prev,
                                whatsappBusinessID: e.target.value,
                              }))
                            }
                          />
                          <Tooltip
                            title={t(
                              "Go to Meta Business Suite (business.facebook.com) > Settings > WhatsApp Accounts. The Business ID is shown in the URL or in the account details.",
                            )}
                            arrow
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "1.1rem",
                                color: "#999",
                                cursor: "pointer",
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <TextField
                            label={t("Whatsapp Phone ID")}
                            fullWidth
                            size="small"
                            margin="dense"
                            sx={textFieldSx}
                            value={state.whatsappPhoneID}
                            onChange={(e) =>
                              setState((prev) => ({
                                ...prev,
                                whatsappPhoneID: e.target.value,
                              }))
                            }
                          />
                          <Tooltip
                            title={t(
                              "In Meta Business Suite, go to WhatsApp > Configuration > Phone Numbers. Click on your number to see the Phone Number ID.",
                            )}
                            arrow
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "1.1rem",
                                color: "#999",
                                cursor: "pointer",
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <TextField
                            label={t("Access Token")}
                            fullWidth
                            size="small"
                            margin="dense"
                            type="password"
                            sx={textFieldSx}
                            value={state.accessToken}
                            onChange={(e) =>
                              setState((prev) => ({
                                ...prev,
                                accessToken: e.target.value,
                              }))
                            }
                          />
                          <Tooltip
                            title={t(
                              "In Meta Developers (developers.facebook.com), open your app > WhatsApp > API Setup. Generate or copy the Access Token from there.",
                            )}
                            arrow
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "1.1rem",
                                color: "#999",
                                cursor: "pointer",
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={state.isNewPhoneNumber}
                                onChange={(e) =>
                                  setState((prev) => ({
                                    ...prev,
                                    isNewPhoneNumber: e.target.checked,
                                    registerPin: e.target.checked
                                      ? prev.registerPin
                                      : "",
                                  }))
                                }
                                size="small"
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ color: isDark ? "#e0e0e0" : undefined }}
                              >
                                {t("Is this a new phone number?")}
                              </Typography>
                            }
                            sx={{ mt: 1 }}
                          />
                          <Tooltip
                            title={t(
                              "Check this if the phone number has never been registered with WhatsApp Business API before. A 6-digit PIN will be required for two-step verification during registration.",
                            )}
                            arrow
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "1.1rem",
                                color: "#999",
                                cursor: "pointer",
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        {state.isNewPhoneNumber && (
                          <TextField
                            label={t("Registration PIN (6 digits)")}
                            fullWidth
                            size="small"
                            margin="dense"
                            value={state.registerPin}
                            onChange={(e) => {
                              const val = e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 6);
                              setState((prev) => ({
                                ...prev,
                                registerPin: val,
                              }));
                            }}
                            inputProps={{
                              maxLength: 6,
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                            }}
                            helperText={t(
                              "Choose any 6-digit PIN. This PIN is used for two-step verification. Remember it for future use.",
                            )}
                            sx={textFieldSx}
                          />
                        )}
                      </>
                    )}
                    {channel === "API_OFFICIAL" && (
                      <Box sx={{ mt: 1, textAlign: "center" }}>
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => setUseManualSetup((prev) => !prev)}
                          sx={{ fontSize: 13 }}
                        >
                          {useManualSetup
                            ? t("Use Embedded Signup instead")
                            : t("Enter IDs manually instead")}
                        </Link>
                      </Box>
                    )}
                    {channel === "API_OFFICIAL" && !useManualSetup ? (
                      <Box sx={{ px: 3, pb: 2, pt: 1 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          sx={{
                            backgroundColor: "#1877F2",
                            color: "#ffffff",
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: "0.95rem",
                            height: 44,
                            borderRadius: "6px",
                            position: "relative",
                            overflow: "hidden",
                            pl: 0,
                            "&:hover": { backgroundColor: "#1464CC" },
                          }}
                        >
                          {/* Icon badge – darker left section */}
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 44,
                              backgroundColor: "#166FE5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FacebookIcon
                              sx={{ color: "#ffffff", fontSize: "1.4rem" }}
                            />
                          </Box>
                          <Box
                            sx={{
                              pl: "44px",
                              width: "100%",
                              textAlign: "center",
                            }}
                          >
                            {t("Connect with Facebook")}
                          </Box>
                        </Button>
                        <Button
                          onClick={handleToggle}
                          fullWidth
                          sx={{
                            mt: 1,
                            textTransform: "none",
                            color: isDark ? "#a0a0a0" : "text.secondary",
                            fontSize: "0.85rem",
                          }}
                        >
                          {t("back")}
                        </Button>
                      </Box>
                    ) : (
                      <DialogActions sx={modalStyles.actions}>
                        <Button onClick={handleToggle}>{t("back")}</Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={
                            channel === "Waha" ? <LiaQrcodeSolid /> : undefined
                          }
                        >
                          {channel === "Waha" ? t("Scan QR Code") : t("Save")}
                        </Button>
                      </DialogActions>
                    )}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Container>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {state.showQrWarning && (
          <Dialog
            open
            onClose={() => setState((p) => ({ ...p, showQrWarning: false }))}
            maxWidth="xs"
            fullWidth
            PaperProps={{ style: modalStyles.paper, sx: darkPaper }}
          >
            <DialogTitle sx={{ color: isDark ? "#ffffff" : undefined }}>
              {t("Atenção")}
            </DialogTitle>
            <DialogContent>
              <Typography>
                {t(
                  "Please scan the QR Code to start a WhatsApp session. If you close it, you'll need to click the card again to recover it. Do you want to close it?",
                )}
              </Typography>
            </DialogContent>
            <DialogActions sx={modalStyles.actions}>
              <Button
                onClick={() =>
                  setState((p) => ({ ...p, showQrWarning: false }))
                }
              >
                {t("Cancelar")}
              </Button>
              <Button onClick={handleConfirmCloseWarning} color="error">
                {t("Fechar")}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </AnimatePresence>

      <WebhookConfigDialog
        open={showWebhookConfig}
        onClose={() => {
          setShowWebhookConfig(false);
          handleToggle();
        }}
      />

      <ToastContainer />
    </>
  );
};
